/** @module @liquidefy/code-editor/editor/editor-accessibility @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  Position,
  AccessibilityConfig,
  AccessibilityInfo,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — accessibility state, modes, ARIA
// ═══════════════════════════════════════════════════════════════════════════════

/** Screen reader optimization mode. */
export type ScreenReaderMode =
  | 'off'       // No screen reader optimizations
  | 'on'        // Full screen reader mode (simplified DOM, verbose aria)
  | 'auto';     // Detected from OS accessibility APIs

/** High contrast theme variant for visual accessibility. */
export type HighContrastMode =
  | 'none'
  | 'high-contrast-dark'
  | 'high-contrast-light';

/** ARIA live region politeness level. */
export type AriaLive = 'off' | 'polite' | 'assertive';

/** Keyboard navigation mode within the editor. */
export type KeyboardNavigationMode =
  | 'editor'        // Standard editor keyboard nav (arrow keys move cursor)
  | 'accessibility' // Tab-focus mode — Tab moves between UI elements
  | 'vim';          // Vim modal navigation

/**
 * A queued announcement to be surfaced to a screen reader via ARIA live region.
 */
export interface ScreenReaderAnnouncement {
  readonly id: string;
  readonly message: string;
  readonly politeness: AriaLive;
  readonly timestamp: number;
}

/**
 * Full accessibility state for an editor instance.
 * This is separate from AccessibilityConfig (which is static configuration)
 * and represents the live runtime state of accessibility features.
 */
export interface AccessibilityState {
  readonly config: AccessibilityConfig;
  readonly screenReaderMode: ScreenReaderMode;
  readonly highContrastMode: HighContrastMode;
  readonly reducedMotion: boolean;
  readonly keyboardNavigationMode: KeyboardNavigationMode;
  readonly pendingAnnouncements: ScreenReaderAnnouncement[];
  readonly lastAnnouncedAt: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY — create initial accessibility state
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create an initial AccessibilityState from a config object.
 * Derives initial modes from the config flags.
 *
 * @param config The static accessibility configuration.
 */
export function createAccessibilityState(config: AccessibilityConfig): AccessibilityState {
  const screenReaderMode: ScreenReaderMode =
    config.accessibilitySupport === 'on'
      ? 'on'
      : config.accessibilitySupport === 'off'
      ? 'off'
      : 'auto';

  const highContrastMode: HighContrastMode = config.highContrast
    ? 'high-contrast-dark'
    : 'none';

  return {
    config,
    screenReaderMode,
    highContrastMode,
    reducedMotion: config.reducedMotion,
    keyboardNavigationMode: config.tabFocusMode ? 'accessibility' : 'editor',
    pendingAnnouncements: [],
    lastAnnouncedAt: 0,
  };
}

/**
 * Validate an AccessibilityConfig object, returning an error when required
 * fields are missing or out of range.
 *
 * @param config The config to validate.
 */
export function validateAccessibilityConfig(config: AccessibilityConfig): EditorResult<AccessibilityConfig> {
  if (!['auto', 'on', 'off'].includes(config.accessibilitySupport)) {
    return err(
      'INVALID_ACCESSIBILITY_CONFIG',
      `accessibilitySupport must be 'auto', 'on', or 'off'`,
    );
  }
  if (typeof config.accessibilityPageSize !== 'number' || config.accessibilityPageSize < 1) {
    return err(
      'INVALID_ACCESSIBILITY_CONFIG',
      'accessibilityPageSize must be a positive integer',
    );
  }
  if (typeof config.ariaLabel !== 'string') {
    return err('INVALID_ACCESSIBILITY_CONFIG', 'ariaLabel must be a string');
  }
  return ok(config, 'ACCESSIBILITY_CONFIG_VALID', 'Accessibility config is valid');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 SCREEN READER — toggle, mode management
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Enable or disable screen reader optimizations.
 * When enabled: virtual cursor model is used, aria-* attributes are maximally populated,
 * decorative elements are hidden from the accessibility tree.
 *
 * @param state   Current accessibility state.
 * @param enabled Whether to enable screen reader mode.
 */
export function setScreenReaderMode(
  state: AccessibilityState,
  enabled: boolean,
): EditorResult<AccessibilityState> {
  const mode: ScreenReaderMode = enabled ? 'on' : 'off';
  const updatedConfig: AccessibilityConfig = {
    ...state.config,
    screenReaderEnabled: enabled,
    accessibilitySupport: enabled ? 'on' : 'off',
  };
  return ok(
    { ...state, screenReaderMode: mode, config: updatedConfig },
    'SCREEN_READER_MODE_SET',
    `Screen reader mode: ${mode}`,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 ARIA LABELS & DESCRIPTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate an ARIA label string for the editor given the current accessibility
 * context (e.g. the document name, language, dirty state).
 *
 * @param state   Current accessibility state.
 * @param context Contextual data to embed in the label.
 */
export function getAriaLabel(
  state: AccessibilityState,
  context: { readonly documentName?: string; readonly language?: string; readonly isDirty?: boolean },
): string {
  const parts: string[] = [];

  if (state.config.ariaLabel) {
    parts.push(state.config.ariaLabel);
  } else {
    parts.push('Code Editor');
  }

  if (context.documentName) {
    parts.push(context.documentName);
  }
  if (context.language) {
    parts.push(context.language);
  }
  if (context.isDirty) {
    parts.push('modified');
  }
  if (state.config.screenReaderEnabled) {
    parts.push('screen reader optimized');
  }

  return parts.join(', ');
}

/**
 * Generate a verbose ARIA description of the content at a given document position.
 * Used by screen readers to narrate what is at the cursor when moving line-by-line.
 *
 * @param state    Current accessibility state.
 * @param position The position in the document to describe.
 * @param info     Structured info about the content at this position.
 */
export function getAriaDescription(
  state: AccessibilityState,
  position: Position,
  info: AccessibilityInfo,
): string {
  const parts: string[] = [];

  parts.push(`Line ${position.line + 1}, Column ${position.column + 1}`);
  parts.push(`of ${info.linesCount} lines`);

  if (info.selectedText) {
    parts.push(`Selected: ${info.selectedText}`);
  }

  if (info.suggestion) {
    parts.push(`Suggestion: ${info.suggestion}`);
  }

  return parts.join('. ');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 HIGH CONTRAST & REDUCED MOTION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Set the high contrast display mode.
 * The editor's theme layer responds to this to swap the active color theme.
 *
 * @param state Current accessibility state.
 * @param mode  The desired high contrast mode.
 */
export function setHighContrastMode(
  state: AccessibilityState,
  mode: HighContrastMode,
): EditorResult<AccessibilityState> {
  const updatedConfig: AccessibilityConfig = {
    ...state.config,
    highContrast: mode !== 'none',
  };
  return ok(
    { ...state, highContrastMode: mode, config: updatedConfig },
    'HIGH_CONTRAST_MODE_SET',
    `High contrast mode: ${mode}`,
  );
}

/**
 * Enable or disable reduced motion mode.
 * When enabled, all CSS transitions and animations are suppressed.
 *
 * @param state   Current accessibility state.
 * @param enabled Whether reduced motion should be active.
 */
export function setReducedMotion(
  state: AccessibilityState,
  enabled: boolean,
): EditorResult<AccessibilityState> {
  const updatedConfig: AccessibilityConfig = {
    ...state.config,
    reducedMotion: enabled,
  };
  return ok(
    { ...state, reducedMotion: enabled, config: updatedConfig },
    'REDUCED_MOTION_SET',
    `Reduced motion: ${enabled}`,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 KEYBOARD NAVIGATION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Return the current keyboard navigation mode.
 *
 * @param state Current accessibility state.
 */
export function getKeyboardNavigationMode(state: AccessibilityState): KeyboardNavigationMode {
  return state.keyboardNavigationMode;
}

/**
 * Switch the keyboard navigation mode.
 * In `accessibility` mode, Tab navigates between UI elements rather than
 * inserting a tab character.
 *
 * @param state Current accessibility state.
 * @param mode  The desired navigation mode.
 */
export function setKeyboardNavigationMode(
  state: AccessibilityState,
  mode: KeyboardNavigationMode,
): EditorResult<AccessibilityState> {
  const updatedConfig: AccessibilityConfig = {
    ...state.config,
    tabFocusMode: mode === 'accessibility',
  };
  return ok(
    { ...state, keyboardNavigationMode: mode, config: updatedConfig },
    'KEYBOARD_NAV_MODE_SET',
    `Keyboard navigation mode: ${mode}`,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 SCREEN READER ANNOUNCEMENTS — queue, flush
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Queue an announcement to be surfaced to the screen reader.
 * The message will be read via an ARIA live region with the given politeness.
 *
 * Use `polite` for informational updates and `assertive` for urgent notifications
 * (e.g. errors, critical warnings). Excessive `assertive` announcements are
 * disruptive — prefer `polite` unless the user must be interrupted immediately.
 *
 * @param state      Current accessibility state.
 * @param message    The text to announce.
 * @param politeness ARIA live region politeness level.
 */
export function announceToScreenReader(
  state: AccessibilityState,
  message: string,
  politeness: AriaLive = 'polite',
): EditorResult<AccessibilityState> {
  if (!message.trim()) {
    return err('EMPTY_ANNOUNCEMENT', 'Screen reader announcement must not be empty');
  }

  const announcement: ScreenReaderAnnouncement = {
    id: generateId('ann'),
    message,
    politeness,
    timestamp: now(),
  };

  return ok(
    {
      ...state,
      pendingAnnouncements: [...state.pendingAnnouncements, announcement],
    },
    'ANNOUNCEMENT_QUEUED',
    'Screen reader announcement queued',
  );
}

/**
 * Flush all pending announcements, returning them and clearing the queue.
 * The caller is responsible for rendering them into the DOM's live regions.
 *
 * @param state Current accessibility state.
 */
export function flushAnnouncements(
  state: AccessibilityState,
): { readonly state: AccessibilityState; readonly announcements: ScreenReaderAnnouncement[] } {
  return {
    state: { ...state, pendingAnnouncements: [], lastAnnouncedAt: now() },
    announcements: [...state.pendingAnnouncements],
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 SUMMARY
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Produce a human-readable summary of all active accessibility settings.
 * Useful for diagnostic panels and accessibility preference UIs.
 *
 * @param state Current accessibility state.
 */
export function getAccessibilitySummary(state: AccessibilityState): Record<string, unknown> {
  return {
    screenReaderMode: state.screenReaderMode,
    highContrastMode: state.highContrastMode,
    reducedMotion: state.reducedMotion,
    tabFocusMode: state.config.tabFocusMode,
    keyboardNavigationMode: state.keyboardNavigationMode,
    ariaLabel: state.config.ariaLabel,
    accessibilityPageSize: state.config.accessibilityPageSize,
    pendingAnnouncementCount: state.pendingAnnouncements.length,
  };
}
